import { Directive, ElementRef, Input, OnChanges, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ContentService } from '@blk/cwp-ng-framework';
import { ApplicationPathConstants } from '@ddt-web/config';
import { ErrorPopupService, PopupDialogObject, PopupDialogOptions } from '@ddt-web/error-handler';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { combineLatest } from 'rxjs';

import { ComponentData } from '../models/component-data';

@UntilDestroy()
@Directive({
	// eslint-disable-next-line @angular-eslint/directive-selector
	selector: '[autoScroller]'
})
export class AutoScrollerDirective implements OnInit, OnChanges {
	@Input() public data: ComponentData;
	@Input() public tabId: string;
	@Input() public ignoreFailedEvent: boolean = false;
	@Input() public continousScroll: boolean = false;
	private fragment: string;
	public queryParams: any;
	private isScrolling: boolean = false;

	constructor(
		private route: ActivatedRoute,
		private router: Router,
		private errorPopupService: ErrorPopupService,
		private contentService: ContentService,
		private elemRef: ElementRef
	) { }

	public ngOnInit(): void {
		combineLatest(this.route.fragment, this.route.queryParams)
			.pipe(untilDestroyed(this))
			.subscribe(([fragment, params]) => {
				this.fragment = fragment;
				this.queryParams = params;
				this.handleScrollEvent();
			});
	}
	public ngOnChanges(): void {
		this.handleScrollEvent();
	}

	public handleScrollEvent(): void {
		if (this.data && this.fragment) {
			if (this.data.success && this.data.tab[this.fragment]) {
				if (this.fragment === this.tabId) {
					this.scrollToMe();
				}
			} else if (!this.ignoreFailedEvent) {
				// TODO: this method need to be changed, each autoscroll target in the same page will all fire redirect
				this.redirectToOverview();
			}
		}
	}

	public scrollToMe(): void {
		if (this.fragment && !this.isScrolling) {
			this.isScrolling = true;
			setTimeout(() => {
				try {
					this.isScrolling = false;
					this.elemRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
				} catch (e) {
					// do nothing, this has nothing to do with state of the component, shouldn't throw any error even if is failed
				}
			}, 1000);
			/**
			 * The following code is added due to asynchronous nature of the page
			 * In certain cases, even though the page scrolls to the targeted section, another section loads above the targeted one after scroll and pushes it down.
			 * With the following code, we keep scrolling back to the targeted section, in order to make sure that the page always lands on the targeted view.
			 */
			if (this.continousScroll) {
				let timesRun = 0;
				const interval = setInterval(() => {
					try {
						this.scrollTargetInView(timesRun++, interval);
					}
					catch (e) {
						// do nothing, this has nothing to do with state of the component, shouldn't throw any error even if is failed
					}
				}, 1000);
			}
		}
	}

	public scrollTargetInView(timesRun: number, interval): void {
		this.elemRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
		if (timesRun === 2) {
			clearInterval(interval);
		}
	}

	public redirectToOverview(): void {
		if (!this.isScrolling) {
			const redirectingMessage = PopupDialogObject.create({
				title: this.contentService.get('AUTO_SCROLL_REDIRECT_TITLE', 'Redirecting...'),
				description: this.contentService.get('AUTO_SCROLL_REDIRECT_DESCRIPTION', 'Section is not available, redirecting to overview'),
				showSpinner: true
			} as PopupDialogOptions);
			this.errorPopupService.openResponseMessageDialog(redirectingMessage);
			this.isScrolling = true;
			setTimeout(() => {
				this.isScrolling = false;
				this.errorPopupService.closeAllDialogs();
				this.router.navigate([ApplicationPathConstants.OVERVIEW_TAB_PATH], {
					queryParams: this.queryParams
				});
			}, 2000);
		}
	}
}
